import React, { useEffect, useMemo, useState } from 'react';

import { ContentBlock, ContentPayload, RendererConfig, TextFormattingOptions } from '../types';
import { BlockRendererFactory } from './BaseBlockRenderer';

interface ContentRendererProps {
  content: ContentPayload;
  config?: Partial<RendererConfig>;
  textOptions?: Partial<TextFormattingOptions>;
  className?: string;
}

const defaultConfig: RendererConfig = {
  enableAnimations: true,
  theme: 'light',
  spacing: 'normal',
};

const defaultTextOptions: TextFormattingOptions = {
  enableMarkdown: true,
  enableCodeHighlighting: true,
  enableLinks: true,
};

export const ContentRenderer: React.FC<ContentRendererProps> = ({
  content,
  config = {},
  textOptions = {},
  className = '',
}) => {
  const mergedConfig = useMemo(() => ({ ...defaultConfig, ...config }), [config]);
  const mergedTextOptions = useMemo(
    () => ({ ...defaultTextOptions, ...textOptions }),
    [textOptions],
  );

  const rendererFactory = useMemo(
    () => new BlockRendererFactory(mergedConfig, mergedTextOptions),
    [mergedConfig, mergedTextOptions],
  );

  const processedContent = useMemo(() => {
    try {
      if (!content || !Array.isArray(content)) return [];

      const isNested = content.length > 0 && Array.isArray(content[0]);

      if (isNested) {
        return (content as ContentBlock[][]).flat();
      } else {
        return content as ContentBlock[];
      }
    } catch (error) {
      console.error('Content processing error:', error);
      return [];
    }
  }, [content]);

  const [renderedBlocks, setRenderedBlocks] = useState<React.ReactElement[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const renderBlocks = async () => {
      if (!processedContent.length) {
        setRenderedBlocks([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const renderedElements: React.ReactElement[] = [];
        let claimGroup: React.ReactElement[] = [];

        const flushClaimGroup = () => {
          if (claimGroup.length > 0) {
            renderedElements.push(
              <div key={`claim-group-${renderedElements.length}`} className="flex flex-wrap gap-4 justify-center">
                {claimGroup}
              </div>,
            );
            claimGroup = [];
          }
        };

        for (let index = 0; index < processedContent.length; index++) {
          const block = processedContent[index];
          try {
            const renderer = await rendererFactory.createRenderer(block.type);
            const element = renderer.render(block, index);

            if (block.type === 'claim' || block.type === 'gap') {
              claimGroup.push(element);
            } else {
              flushClaimGroup();
              renderedElements.push(element);
            }
          } catch (blockError) {
            console.error(`Error rendering block ${index}:`, blockError);
            flushClaimGroup();
            const errorElement = (
              <div
                key={`error-${index}`}
                className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700"
              >
                <div className="font-medium">Error rendering block {index + 1}</div>
                <div className="text-sm mt-1">
                  {blockError instanceof Error ? blockError.message : 'Unknown error'}
                </div>
              </div>
            );
            renderedElements.push(errorElement);
          }
        }

        flushClaimGroup();
        setRenderedBlocks(renderedElements);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to render content');
      } finally {
        setIsLoading(false);
      }
    };

    renderBlocks();
  }, [processedContent, rendererFactory]);

  if (!processedContent.length) {
    return (
      <div className="flex items-center justify-center p-8 text-neutral-50 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
        <div className="text-center">
          <div className="text-lg font-medium mb-2">No content to display</div>
          <div className="text-sm">Enter valid content data to see the preview</div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8 text-neutral-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <div className="text-sm">Loading content...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
        <div className="font-medium">Error rendering content</div>
        <div className="text-sm mt-1">{error}</div>
      </div>
    );
  }

  return (
    <div className={`content-renderer ${className}`}>
      <div className="space-y-4 animate-stagger">{renderedBlocks}</div>
    </div>
  );
};

export default ContentRenderer;
